<template>
  <div class="row">
    <div class="col-xl-8 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>Pipeline Stages</h5>
            <small class="text-muted">Click a bar to drill down</small>
          </div>
        </div>

        <div class="card-body pb-0">
          <transition name="fade-slide" mode="out-in">
            <div v-if="loading" class="p-3" key="skel_stage">
              <div class="skel skel-line skel-gap-12" style="width: 40%"></div>
              <div class="skel skel-line skel-gap-12" style="width: 65%"></div>
              <div class="skel skel-chart"></div>
            </div>

            <div v-else key="content_stage">
              <div class="row g-2 justify-content-center mb-3">
                <div
                  v-for="s in stageRows"
                  :key="s.stage"
                  class="col-md col-sm-4 col-6"
                >
                  <div
                    class="border rounded p-2 hover-pointer"
                    @click="drillStage(s.stage)"
                  >
                    <p class="mb-1">
                      <i class="ti ti-point-filled text-primary"></i
                      >{{ s.stage }}
                    </p>
                    <h6 class="mb-0">{{ fmtNum(s.count) }}</h6>
                  </div>
                </div>
              </div>

              <div id="lead_stage_chart">
                <apexchart
                  type="bar"
                  height="230"
                  :options="stageOptions"
                  :series="stageSeries"
                />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div class="col-xl-4 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap row-gap-2"
          >
            <h5>New Leads</h5>
            <small class="text-muted">Heatmap by weekday</small>
          </div>
        </div>

        <div class="card-body pb-0">
          <transition name="fade-slide" mode="out-in">
            <div v-if="loading" class="p-3" key="skel_heat">
              <div class="skel skel-line skel-gap-12" style="width: 55%"></div>
              <div class="skel skel-chart"></div>
            </div>

            <div v-else key="content_heat">
              <div id="heat_chart">
                <apexchart
                  type="heatmap"
                  height="300"
                  :options="heatOptions"
                  :series="heatSeries"
                />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useLeadDashboardStore } from "@/stores/leadDashboardStore";

const dash = useLeadDashboardStore();
const loading = computed(() => dash.loading);

const stageRows = computed(() =>
  (dash.leadsByStage || []).map((x) => ({
    stage: x.stage_name || "Unassigned",
    count: Number(x.count || 0),
  }))
);

const stageSeries = computed(() => [
  { name: "Leads", data: stageRows.value.map((x) => x.count) },
]);

const stageOptions = computed(() => ({
  chart: {
    height: 230,
    type: "bar",
    toolbar: { show: false },
    events: {
      dataPointSelection: async (_e, _ctx, cfg) => {
        const idx = cfg?.dataPointIndex ?? -1;
        const stageName = stageRows.value[idx]?.stage;
        if (!stageName) return;
        const ok = dash.setStageByName(stageName);
        if (ok) await dash.fetchDashboard();
      },
    },
  },
  plotOptions: {
    bar: { horizontal: false, endingShape: "rounded", borderRadius: 5 },
  },
  dataLabels: { enabled: false },
  grid: { strokeDashArray: 5, borderColor: "transparent" },
  xaxis: { categories: stageRows.value.map((x) => x.stage) },
}));

async function drillStage(stageName) {
  const ok = dash.setStageByName(stageName);
  if (ok) await dash.fetchDashboard();
}

// Heatmap
const heatSeries = computed(() => dash.heatmap?.series || []);
const heatOptions = computed(() => ({
  chart: { type: "heatmap", height: 300, toolbar: { show: false } },
  dataLabels: { enabled: false },
}));

function fmtNum(v) {
  return Number(v || 0).toLocaleString();
}
</script>

<style scoped>
.hover-pointer {
  cursor: pointer;
}
</style>
